import { useMutation } from '@vue/apollo-composable';
import { useLocalStorage, usePreferredDark } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, watch, watchEffect } from 'vue';

import { useAuthStore } from '@/entities/auth';
import { UpdateMyThemeDocument, UserTheme } from '@shared/api/graphql/gql/graphql';

type ResolvedTheme = 'light' | 'dark';

/** Ключ темы устройства; его же читает inline-скрипт `index.html` до монтирования. */
const STORAGE_KEY = 'mors:theme';

/** `meta[name=theme-color]` повторяет фон страницы `--surface-page` текущей темы. */
const syncThemeColor = (): void => {
  const color = getComputedStyle(document.documentElement).getPropertyValue('--surface-page').trim();
  if (!color) {
    console.warn('[theme-switch] --surface-page пуст');
    return;
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
};

const isUserTheme = (value: unknown): value is UserTheme => Object.values<unknown>(UserTheme).includes(value);

/**
 * Тема админки: выбор на устройстве (`localStorage`) и в аккаунте (`users.theme`).
 * Загруженный `authContext` переписывает устройство темой аккаунта, выход её не сбрасывает.
 * Под impersonation выбор меняет только устройство, а тема сотрудника не применяется.
 */
export const useThemeStore = defineStore('theme', () => {
  const authStore = useAuthStore();
  const preference = useLocalStorage<UserTheme>(STORAGE_KEY, UserTheme.System, {
    serializer: { read: (raw) => (isUserTheme(raw) ? raw : UserTheme.System), write: (value) => value },
  });
  const prefersDark = usePreferredDark();
  const { mutate } = useMutation(UpdateMyThemeDocument);

  const resolved = computed<ResolvedTheme>(() => {
    if (preference.value === UserTheme.Light) return 'light';
    if (preference.value === UserTheme.Dark) return 'dark';
    return prefersDark.value ? 'dark' : 'light';
  });

  watchEffect(() => {
    document.documentElement.dataset.theme = resolved.value;
    syncThemeColor();
  });

  watch(
    () => authStore.user?.theme,
    (theme) => {
      if (theme && !authStore.isImpersonating) preference.value = theme;
    },
    { immediate: true },
  );

  const setPreference = async (theme: UserTheme): Promise<void> => {
    preference.value = theme;
    if (authStore.isImpersonating) return;

    try {
      await mutate({ theme });
    } catch {
      // Тост показал errorLink, выбор остаётся на устройстве.
    }
  };

  return { preference, resolved, setPreference };
});
